import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Auth APIs (admin gate)
export const authApi = {
  login: async (username, password) => {
    const response = await api.post("/auth/admin-login", { username, password });
    return response.data;
  },
  me: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },
  logout: async () => {
    const response = await api.post("/auth/logout");
    return response.data;
  },
  refresh: async () => {
    const response = await api.post("/auth/refresh");
    return response.data;
  },
};

// Patient APIs
export const patientApi = {
  // Get all patients with optional filters
  getAll: async (params = {}) => {
    const response = await api.get("/patients", { params });
    return response.data;
  },

  // Get single patient by ID
  getById: async (id) => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  // Create new patient
  create: async (data) => {
    const response = await api.post("/patients", data);
    return response.data;
  },

  // Update patient
  update: async (id, data) => {
    const response = await api.put(`/patients/${id}`, data);
    return response.data;
  },

  // Delete patient
  delete: async (id) => {
    const response = await api.delete(`/patients/${id}`);
    return response.data;
  },

  // Toggle marcaj "citit" (independent de ateliere)
  toggleCitit: async (id) => {
    const response = await api.post(`/patients/${id}/toggle-citit`);
    return response.data;
  },

  // Update flag-uri fișe (CU/FĂRĂ EKG, CU/FĂRĂ Analize)
  updateFlags: async (id, payload) => {
    const response = await api.put(`/patients/${id}/flags`, payload);
    return response.data;
  },

  // Update atelier (for 2, 3, 4)
  updateAtelier: async (patientId, atelierNum, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/${atelierNum}`, data);
    return response.data;
  },

  // Update Atelier 1 (Body Composition)
  updateAtelier1: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/1`, data);
    return response.data;
  },

  // Update Atelier 2 (Cardiovascular Adaptation - Ruffier Test)
  updateAtelier2: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/2`, data);
    return response.data;
  },

  // Update Atelier 4 (Examen clinic pe sisteme)
  updateAtelier4: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/4`, data);
    return response.data;
  },

  // Update Atelier 5 (Analize medicale + concluzii + recomandări)
  updateAtelier5: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/5`, data);
    return response.data;
  },

  // Atelier 5 — upload/delete/preview analize PDF
  uploadAtelier5Analiza: async (patientId, file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/patients/${patientId}/atelier/5/analize`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },
  deleteAtelier5Analiza: async (patientId, fileId) => {
    const response = await api.delete(`/patients/${patientId}/atelier/5/analize/${fileId}`);
    return response.data;
  },
  atelier5AnalizaViewUrl: (patientId, fileId) => `${API}/patients/${patientId}/atelier/5/analize/${fileId}`,

  // Coaches (Antrenori) — creare manuală + auto-match athletes
  listCoaches: async () => (await api.get("/coaches")).data,
  createCoach: async (payload) => (await api.post("/coaches", payload)).data,
  updateCoach: async (id, payload) => (await api.put(`/coaches/${id}`, payload)).data,
  deleteCoach: async (id) => (await api.delete(`/coaches/${id}`)).data,
  coachAthletes: async (id) => (await api.get(`/coaches/${id}/athletes`)).data,
  addAthleteToCoach: async (coachId, patientId) =>
    (await api.post(`/coaches/${coachId}/athletes/${patientId}`)).data,
  removeAthleteFromCoach: async (coachId, patientId) =>
    (await api.delete(`/coaches/${coachId}/athletes/${patientId}`)).data,
  searchPatients: async (search) =>
    (await api.get("/patients", { params: { search } })).data,
  createReferatSnapshot: async (patientId, html, numar_viza) =>
    (await api.post(`/patients/${patientId}/referat/snapshot`, { html, numar_viza })).data,
  createConcluziiSnapshot: async (patientId, html, numar_viza) =>
    (await api.post(`/patients/${patientId}/concluzii/snapshot`, { html, numar_viza })).data,
  deleteReferatSnapshot: async (patientId, snapshotId) =>
    (await api.delete(`/patients/${patientId}/referat/snapshot/${snapshotId}`)).data,
  deleteConcluziiSnapshot: async (patientId, snapshotId) =>
    (await api.delete(`/patients/${patientId}/concluzii/snapshot/${snapshotId}`)).data,
  eligibleFotbal: async () => (await api.get("/reports/fotbal-eligible")).data,
  dailyReport: async (date) => (await api.get("/reports/daily", { params: { date } })).data,
  getPatient: async (id) => (await api.get(`/patients/${id}`)).data,

  // WHO Growth (5-19 ani) — percentile IMC + talie
  whoGrowth: async (patientId) => {
    const response = await api.get(`/patients/${patientId}/who-growth`);
    return response.data;
  },

  // AI generare Concluzii aviz medico-sportiv (Gemini text)
  aiGenerateAviz: async (patientId, postSportiv = "") => {
    const response = await api.post(
      `/patients/${patientId}/aviz/ai-generate`,
      { post_sportiv: postSportiv },
      { timeout: 90000 },
    );
    return response.data;
  },

  // Atelier 5 — AI analysis (Gemini vision on the uploaded PDF)
  aiAnalyzeAnaliza: async (patientId, fileId) => {
    const response = await api.post(
      `/patients/${patientId}/atelier/5/analize/${fileId}/ai-analyze`,
      null,
      { timeout: 90000 }, // AI-ul poate dura până la 30-60s
    );
    return response.data;
  },

  // Update Atelier 3 (SCHILLER ECG)
  updateAtelier3: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/atelier/3`, data);
    return response.data;
  },

  // Generează numărul de viză (necesită toate cele 4 ateliere completate)
  generateNumarViza: async (patientId) => {
    const response = await api.post(`/patients/${patientId}/generate-viza`);
    return response.data;
  },

  // Șterge o fișă completă (snapshot al unei vize)
  deleteFisa: async (patientId, fisaId) => {
    const response = await api.delete(`/patients/${patientId}/fise/${fisaId}`);
    return response.data;
  },

  // SCHILLER integration
  exportGDTUrl: (patientId) => `${API}/patients/${patientId}/atelier/3/export/gdt`,
  exportXMLUrl: (patientId) => `${API}/patients/${patientId}/atelier/3/export/xml`,
  previewGDT: async (patientId) => {
    const response = await api.get(`/patients/${patientId}/atelier/3/preview/gdt`);
    return response.data;
  },
  previewXML: async (patientId) => {
    const response = await api.get(`/patients/${patientId}/atelier/3/preview/xml`);
    return response.data;
  },
  ecgPdfUrl: (patientId) => `${API}/patients/${patientId}/atelier/3/pdf`,

  uploadEcgPdf: async (patientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/patients/${patientId}/atelier/3/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadEcgXml: async (patientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/patients/${patientId}/atelier/3/upload-xml`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteEcg: async (patientId) => {
    const response = await api.delete(`/patients/${patientId}/atelier/3/ecg`);
    return response.data;
  },

  getIntegrationLogs: async (patientId, limit = 20) => {
    const response = await api.get(`/integrations/logs`, { params: { patient_id: patientId, limit } });
    return response.data;
  },

  // Update Referat medical (concluzii + recomandări/observații custom)
  updateReferat: async (patientId, data) => {
    const response = await api.put(`/patients/${patientId}/referat`, data);
    return response.data;
  },

  // Add medical review
  addReview: async (patientId, data) => {
    const response = await api.post(`/patients/${patientId}/reviews`, data);
    return response.data;
  },

  // Delete review
  deleteReview: async (patientId, reviewId) => {
    const response = await api.delete(`/patients/${patientId}/reviews/${reviewId}`);
    return response.data;
  },

  // Delete istoric ateliere entry
  deleteIstoricEntry: async (patientId, entryId) => {
    const response = await api.delete(`/patients/${patientId}/istoric/${entryId}`);
    return response.data;
  },

  // Export to CSV
  exportCSV: async () => {
    const response = await api.get("/patients/export/csv", {
      responseType: "blob",
    });
    return response.data;
  },
};

// Attachments (Fișiere/Poze)
export const attachmentsApi = {
  // --- Public (formular sportiv, înainte de submit) ---
  publicUpload: async (sessionId, file, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/public/attachments`, formData, {
      params: { session_id: sessionId },
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },
  publicList: async (sessionId) => {
    const response = await api.get(`/public/attachments`, { params: { session_id: sessionId } });
    return response.data;
  },
  publicDelete: async (sessionId, fileId) => {
    const response = await api.delete(`/public/attachments/${fileId}`, {
      params: { session_id: sessionId },
    });
    return response.data;
  },

  // --- Admin (fișă pacient) ---
  adminUpload: async (patientId, file, onProgress, revenireId) => {
    const formData = new FormData();
    formData.append("file", file);
    if (revenireId) formData.append("revenire_id", revenireId);
    const response = await api.post(`/patients/${patientId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return response.data;
  },
  adminDelete: async (patientId, fileId) => {
    const response = await api.delete(`/patients/${patientId}/attachments/${fileId}`);
    return response.data;
  },
  // URL direct pentru inline view (necesită credentials/cookies deja setate)
  adminViewUrl: (patientId, fileId) => `${API}/patients/${patientId}/attachments/${fileId}`,
};

// Reveniri API — note post-consultație (text + fișiere atașate).
export const revenireApi = {
  create: async (patientId, text = "") => {
    const response = await api.post(`/patients/${patientId}/reveniri`, { text });
    return response.data;
  },
  update: async (patientId, revenireId, text) => {
    const response = await api.put(`/patients/${patientId}/reveniri/${revenireId}`, { text });
    return response.data;
  },
  delete: async (patientId, revenireId) => {
    const response = await api.delete(`/patients/${patientId}/reveniri/${revenireId}`);
    return response.data;
  },
};

// Stats API
export const statsApi = {
  get: async () => {
    const response = await api.get("/stats");
    return response.data;
  },
};

export default api;
